<template>
	<Dialog :open="isOpen" @update:open="$emit('update:isOpen', $event)">
		<DialogContent class="max-h-[80vh] max-w-2xl" :disable-overlay-close="true" @close="handleClose">
			<DialogHeader>
				<DialogTitle>
					<span class="flex items-center gap-2">
						<span class="fa-light fa-file-plus text-green-500"></span>
						Create Flow from Template
					</span>
				</DialogTitle>
				<DialogDescription>Create a new flow based on the "{{ template?.name }}" template</DialogDescription>
			</DialogHeader>

			<div class="mb-4 flex items-center justify-between">
				<div class="flex items-center">
					<div v-for="(step, index) in steps" :key="index" class="flex items-center">
						<div
							class="flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium"
							:class="[
								currentStep > index
									? 'bg-green-500 text-white'
									: currentStep === index
										? 'bg-blue-500 text-white'
										: 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-300'
							]"
						>
							{{ index + 1 }}
						</div>
						<div
							v-if="index < steps.length - 1"
							class="h-1 w-8"
							:class="[currentStep > index ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700']"
						></div>
					</div>
				</div>
				<div class="text-sm text-gray-500 dark:text-gray-400">
					Step {{ currentStep + 1 }} of {{ steps.length }}
				</div>
			</div>

			<div class="mt-4 max-h-[min(80vh,600px)] overflow-y-auto">
				<div v-if="currentStep === 0" class="space-y-4">
					<div class="formGroup mb-4 w-full">
						<p class="filter-label">Flow Name</p>
						<input v-model="newFlow.name" type="text" placeholder="Flow Name" class="w-full" />
					</div>

					<div v-if="tenants?.length > 1" class="w-full">
						<p class="filter-label">Tenant</p>
						<div class="formGroup withIcon">
							<select v-model="newFlow.tenantId" @change="newFlow.accountId = null">
								<option :value="null" selected>-- Please select a Tenant --</option>
								<option v-for="tenant in tenants" :key="tenant.id" :value="tenant.id">
									{{ tenant.companyName }}
								</option>
							</select>
							<span class="fa-light fa-chevron-down"></span>
						</div>
					</div>

					<div v-if="newFlow.tenantId && tenants?.length" class="w-full">
						<p class="filter-label">Account</p>
						<div class="formGroup withIcon">
							<select v-model="newFlow.accountId" aria-label="Selected Account">
								<option :value="null" selected>-- Please select an Account --</option>
								<option v-for="account in tenantAccounts" :key="account.id" :value="account.id">
									{{ account.name }}
								</option>
							</select>
							<span class="fa-light fa-chevron-down"></span>
						</div>
					</div>

					<div
						v-if="template?.description"
						class="mt-4 rounded-md border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800"
					>
						<p class="text-sm text-gray-500 dark:text-gray-400">Template Description:</p>
						<p class="dark:text-white">
							{{ template?.description || 'A template for creating new flows' }}
						</p>
					</div>
				</div>

				<div v-if="currentStep === 1" class="step-content">
					<div v-for="(inputs, group) in filteredInputsByGroup" :key="group" class="mb-6">
						<div class="mb-4 mt-2 w-full border-b border-dark dark:border-light">
							<h3 class="mb-2 text-lg font-semibold">{{ group }}</h3>
						</div>
						<div class="flex w-full flex-wrap gap-2">
							<div
								v-for="input in inputs"
								:key="input.name"
								class="px-2"
								:class="input.fullWidth ? 'w-full' : 'w-full md:w-[calc(50%-0.25rem)]'"
							>
								<div
									class="formGroup mb-2 flex flex-col md:mb-5"
									:class="[
										input.type === 'checkbox' ? 'flex-row items-center gap-2' : '',
										['select', 'nodes', 'siptrunk', 'mobilesim', 'file'].includes(input.type)
											? 'withIcon'
											: ''
									]"
								>
									<label class="textGrey truncate" :for="input.name">{{ input.label }}</label>

									<input
										v-if="
											['text', 'number', 'email', 'tel', 'url', 'password'].includes(input.type)
										"
										:id="input.name"
										v-model="nodeConfig[input.nodeType][input.name]"
										:type="input.type"
										class="form-control"
										:placeholder="input.placeholder || ''"
									/>

									<textarea
										v-else-if="input.type === 'textarea'"
										:id="input.name"
										v-model="nodeConfig[input.nodeType][input.name]"
										class="form-control"
										:placeholder="input.placeholder || ''"
									></textarea>

									<input
										v-else-if="input.type === 'boolean' || input.type === 'checkbox'"
										:id="input.name"
										v-model="nodeConfig[input.nodeType][input.name]"
										type="checkbox"
										class="tw-toggle"
									/>

									<select
										v-else-if="input.type === 'select'"
										:id="input.name"
										v-model="nodeConfig[input.nodeType][input.name]"
									>
										<option
											v-for="option in input.options"
											:key="option.value"
											:value="option.value"
										>
											{{ option.label }}
										</option>
									</select>

									<div v-else-if="input.type === 'siptrunk'" class="flex flex-col gap-1">
										<div class="flex items-center gap-2">
											<select v-model="nodeConfig[input.nodeType][input.name]">
												<option :value="undefined" selected>-- Select a SIP Trunk --</option>
												<option v-for="trunk in siptrunks" :key="trunk.id" :value="trunk.id">
													{{ trunk.username }}
												</option>
											</select>
										</div>
									</div>

									<div v-else-if="input.type === 'mobilesim'" class="flex flex-col gap-1">
										<div class="flex items-center gap-2">
											<select v-model="nodeConfig[input.nodeType][input.name]">
												<option :value="undefined" selected>-- Select a Mobile SIM --</option>
												<option v-for="sim in mobileSims" :key="sim.id" :value="sim.id">
													{{ sim.number }}{{ sim.description ? ' - ' + sim.description : '' }}
												</option>
											</select>
										</div>
									</div>

									<div v-else-if="input.type === 'file'" class="flex flex-col gap-1">
										<div class="flex items-center gap-2">
											<div class="formGroup withIcon relative mr-2 w-full">
												<select v-model="nodeConfig[input.nodeType][input.name]">
													<option :value="undefined" selected>-- Select a file --</option>
													<option
														v-for="file in files"
														:key="file.id"
														:value="file.id + '.' + file.fileExt"
													>
														{{ file.name }}
													</option>
												</select>
												<span class="fa-light fa-chevron-down"></span>
											</div>
											<div class="flex w-4 items-center justify-end gap-2 sm:my-0">
												<button class="w-6" @click="isUploadOpen = true">
													<span class="fa-light fa-upload"></span>
												</button>
											</div>
											<div class="flex w-4 items-center justify-end gap-2 sm:my-0">
												<button class="w-6" @click="isRecordOpen = true">
													<span class="fa-light fa-microphone"></span>
												</button>
											</div>
											<upload-file-audio
												:selected-tenant-id="newFlow.tenantId"
												:selected-account-id="newFlow.accountId"
												:disable-overlay-close="true"
												:loaded-accounts="accounts"
												:open="isUploadOpen"
												@close="
													(created) => onFileUploadClose(input.nodeType, input.name, created)
												"
											/>
											<upload-record-audio
												v-if="isRecordOpen"
												:selected-tenant-id="newFlow.tenantId"
												:selected-account-id="newFlow.accountId"
												:disable-overlay-close="true"
												:open="true"
												:loaded-accounts="accounts"
												@close="
													(created) => onFileUploadClose(input.nodeType, input.name, created)
												"
											/>
										</div>
									</div>

									<div v-else-if="input.type === 'code'" class="w-full">
										<v-ace-editor
											v-model:value="nodeConfig[input.nodeType][input.name]"
											:lang="'javascript'"
											:theme="isDarkMode ? 'github_dark' : 'chrome'"
											:min-lines="input.minLines || 15"
											:max-lines="input.maxLines || 30"
											class="w-full"
										/>
									</div>

									<div v-else-if="input.type === 'json'" class="w-full">
										<v-ace-editor
											v-model:value="nodeConfig[input.nodeType][input.name]"
											:lang="'json'"
											:theme="isDarkMode ? 'github_dark' : 'chrome'"
											:min-lines="input.minLines || 15"
											:max-lines="input.maxLines || 30"
											class="w-full"
										/>
									</div>

									<div
										v-else-if="input.type === 'time_condition' || input.type === 'timeconditions'"
										class="w-full"
									>
										<time-condition-view
											:saved-data="nodeConfig[input.nodeType][input.name]"
											@update="(data) => (nodeConfig[input.nodeType][input.name] = data)"
										/>
									</div>

									<div v-else-if="input.type === 'multi'" class="flex w-full flex-col gap-1">
										<div
											v-for="(option, index) in nodeConfig[input.nodeType][input.name]"
											:key="index"
											class="flex w-full items-center gap-2"
										>
											<input
												:id="`${input.name}-${index}`"
												v-model="nodeConfig[input.nodeType][input.name][index]"
												type="text"
												class="form-control w-full"
												:placeholder="input.placeholder || ''"
											/>
											<button
												class="jmiBtn red"
												@click="nodeConfig[input.nodeType][input.name].splice(index, 1)"
											>
												<span class="fa-light fa-remove"></span>
											</button>
										</div>
										<div
											v-if="
												!nodeConfig[input.nodeType][input.name] ||
												nodeConfig[input.nodeType][input.name]?.length === 0
											"
											class="flex items-center gap-2"
										>
											<p class="textGrey">No options added</p>
										</div>
										<button
											class="jmiBtn green mt-2"
											:disabled="
												nodeConfig[input.nodeType][input.name]?.length &&
												!nodeConfig[input.nodeType][input.name][
													nodeConfig[input.nodeType][input.name].length - 1
												]
											"
											@click="addOption(input.nodeType, input.name)"
										>
											Add
										</button>
									</div>

									<div v-else-if="input.type === 'multiKeyValue'" class="flex w-full flex-col gap-1">
										<div
											v-for="(option, index) in nodeConfig[input.nodeType][input.name]"
											:key="index"
											class="flex w-full items-center gap-2"
										>
											<div class="form-control w-full">
												<input
													:id="`${input.name}-${index}`"
													v-model="nodeConfig[input.nodeType][input.name][index].key"
													type="text"
													:placeholder="input.keyPlaceholder || 'Key'"
												/>
											</div>
											<div class="form-control w-full">
												<input
													:id="`${input.name}-key-${index}`"
													v-model="nodeConfig[input.nodeType][input.name][index].value"
													type="text"
													:placeholder="input.placeholder || 'Value'"
												/>
											</div>
											<button
												class="jmiBtn red"
												@click="nodeConfig[input.nodeType][input.name].splice(index, 1)"
											>
												<span class="fa-light fa-remove"></span>
											</button>
										</div>
										<div
											v-if="
												!nodeConfig[input.nodeType][input.name] ||
												nodeConfig[input.nodeType][input.name]?.length === 0
											"
											class="flex items-center gap-2"
										>
											<p class="textGrey">
												{{ input.emptyText || 'No options added' }}
											</p>
										</div>
										<button
											class="jmiBtn green mt-2"
											:disabled="
												nodeConfig[input.nodeType][input.name]?.length &&
												!nodeConfig[input.nodeType][input.name][
													nodeConfig[input.nodeType][input.name].length - 1
												].key
											"
											@click="addKeyValue(input.nodeType, input.name)"
										>
											Add
										</button>
									</div>

									<div v-else-if="input.type === 'flow'" class="flex flex-col gap-1">
										<div class="flex items-center gap-2">
											<select v-model="nodeConfig[input.nodeType][input.name]">
												<option :value="undefined">-- Select a flow --</option>
												<option v-for="flow in flows" :key="flow.id" :value="flow.id">
													{{ flow.name }}
												</option>
											</select>
										</div>
									</div>

									<div v-else-if="input.type === 'tts_voices'" class="flex flex-col gap-1">
										<TTSVoiceList
											:saved-data="nodeConfig[input.nodeType][input.name]"
											@update="(voice) => (nodeConfig[input.nodeType][input.name] = voice)"
										/>
									</div>

									<input
										v-else
										:id="input.name"
										v-model="nodeConfig[input.nodeType][input.name]"
										type="text"
										class="form-control"
										:placeholder="input.placeholder || ''"
									/>

									<span
										v-if="['select', 'nodes', 'siptrunk', 'mobilesim'].includes(input.type)"
										class="fa-light fa-chevron-down"
									></span>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div v-if="currentStep === 2" class="space-y-4">
					<div class="rounded-md border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
						<h3 class="mb-2 text-lg font-medium dark:text-white">Configuration Summary</h3>

						<div class="space-y-3">
							<div>
								<p class="text-sm text-gray-500 dark:text-gray-400">Template:</p>
								<p class="font-medium dark:text-white">{{ template?.name || 'Custom Template' }}</p>
							</div>

							<div>
								<p class="text-sm text-gray-500 dark:text-gray-400">Flow Name:</p>
								<p class="font-medium dark:text-white">{{ newFlow.name }}</p>
							</div>

							<div>
								<p class="text-sm text-gray-500 dark:text-gray-400">Tenant:</p>
								<p class="font-medium dark:text-white">
									{{ tenants.find((t) => t.id === newFlow.tenantId)?.companyName || 'None' }}
								</p>
							</div>

							<div>
								<p class="text-sm text-gray-500 dark:text-gray-400">Account:</p>
								<p class="font-medium dark:text-white">
									{{ tenantAccounts.find((a) => a.id === newFlow.accountId)?.name || 'None' }}
								</p>
							</div>

							<div v-if="newFlow.description">
								<p class="text-sm text-gray-500 dark:text-gray-400">Description:</p>
								<p class="dark:text-white">{{ newFlow.description }}</p>
							</div>
						</div>
					</div>
				</div>
			</div>

			<DialogFooter class="mt-6 flex min-w-full flex-row justify-between">
				<div class="flex gap-2">
					<button v-if="currentStep > 0" class="jmiBtn gray" @click="currentStep--">Back</button>
					<button class="jmiBtn red" @click="handleClose">Cancel</button>
				</div>
				<div>
					<button
						v-if="currentStep < steps.length - 1"
						class="jmiBtn blue"
						:disabled="!canProceed()"
						@click="currentStep++"
					>
						Next
					</button>
					<button v-else class="jmiBtn green" :disabled="!canCreate()" @click="createFlow()">
						Create Flow
					</button>
				</div>
			</DialogFooter>
		</DialogContent>
	</Dialog>
</template>

<script>
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog'
import flowsApi from '../scripts/flows/flows.api'
import filesApi from '../scripts/files/files.api'
import siptrunksApi from '../scripts/siptrunks/siptrunks.api'
import mobileSimsApi from '../scripts/mobilesims/mobilesims.api'
import flowTemplatesApi from '../scripts/flowtemplates/flowtemplates.api'
import { VAceEditor } from 'vue3-ace-editor'
import 'ace-builds/src-noconflict/mode-json'
import 'ace-builds/src-noconflict/mode-html'
import 'ace-builds/src-noconflict/mode-javascript'
import 'ace-builds/src-noconflict/theme-chrome'
import 'ace-builds/src-noconflict/theme-github_dark'
import TimeConditionView from './flow/TimeConditionView'
import TTSVoiceList from './flow/TTSVoiceList'
import UploadFileAudio from './UploadFileAudio'
import UploadRecordAudio from './UploadRecordAudio'

export default {
	components: {
		Dialog,
		DialogContent,
		DialogHeader,
		DialogTitle,
		DialogDescription,
		DialogFooter,
		VAceEditor,
		TimeConditionView,
		UploadFileAudio,
		UploadRecordAudio,
		TTSVoiceList
	},
	props: {
		isOpen: {
			type: Boolean,
			default: false
		},
		template: {
			type: Object,
			default: null
		},
		tenants: {
			type: Array,
			default: () => []
		},
		accounts: {
			type: Array,
			default: () => []
		},
		flows: {
			type: Array,
			default: () => []
		}
	},

	emits: ['update:isOpen', 'flow-created'],

	data() {
		return {
			currentStep: 0,
			steps: ['Flow Configuration', 'Template Fields', 'Confirmation'],
			newFlow: {
				name: '',
				description: '',
				tenantId: null,
				accountId: null
			},
			templateFields: {},
			nodeConfig: {},
			files: [],
			siptrunks: [],
			mobileSims: [],
			isUploadOpen: false,
			isRecordOpen: false,
			flowNodes: [],
			tenantOptions: [],
			accountOptions: [],
			nodesLoaded: false,
			templateId: null,
			templateName: '',
			templateDescription: '',
			checkboxFields: {}
		}
	},

	computed: {
		tenantAccounts() {
			if (!this.newFlow.tenantId) return []

			return this.accounts
				.filter((account) => account.tenant?.id === this.newFlow.tenantId)
				.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
		},
		isDarkMode() {
			return document.documentElement.classList.contains('dark')
		},
		templateInputs() {
			const inputs = []

			if (!this.templateFields || !this.nodesLoaded) {
				console.warn('No template fields or nodes loaded for template inputs')
				return inputs
			}

			const processedNodeTypes = new Set()

			Object.keys(this.templateFields).forEach((nodeType) => {
				if (processedNodeTypes.has(nodeType)) return
				processedNodeTypes.add(nodeType)

				const baseNodeType = this.getBaseNodeType(nodeType)

				const nodeDefinition = this.flowNodes.find((node) => node.type === baseNodeType)
				if (!nodeDefinition) {
					return
				}

				const groupName = nodeDefinition.name || nodeType

				this.templateFields[nodeType].forEach((fieldName) => {
					const fieldDef =
						(nodeDefinition.fields && nodeDefinition.fields.find((f) => f.name === fieldName)) ||
						(nodeDefinition.properties && nodeDefinition.properties.find((p) => p.name === fieldName))

					if (!fieldDef) {
						this.ensureNodeConfigExists(nodeType)
						if (this.nodeConfig[nodeType][fieldName] === undefined) {
							this.nodeConfig[nodeType][fieldName] = ''
						}

						const defaultInput = {
							name: fieldName,
							nodeType: nodeType,
							label: fieldName,
							type: 'text',
							placeholder: '',
							fullWidth: false,
							group: groupName
						}

						inputs.push(defaultInput)
						return
					}

					const input = {
						name: fieldName,
						nodeType: nodeType,
						label: fieldDef.label || fieldName,
						type: this.getFieldType(nodeType, fieldName),
						placeholder: fieldDef.placeholder || '',
						keyPlaceholder: fieldDef.keyPlaceholder || 'Key',
						emptyText: fieldDef.emptyText || 'No options added',
						fullWidth:
							fieldDef.fullWidth ||
							['code', 'json', 'time_condition', 'textarea'].includes(
								this.getFieldType(nodeType, fieldName)
							),
						minLines: fieldDef.minLines || 15,
						maxLines: fieldDef.maxLines || 30,
						options: this.getFieldOptions(nodeType, fieldName),
						group: groupName,
						dependsOn: fieldDef.dependsOn
					}

					const isRouteCallField =
						baseNodeType === 'routecall' &&
						[
							'advanced_routing',
							'routing_style',
							'destinations',
							'destination',
							'whisper_prompt',
							'timeout'
						].includes(fieldName)
					const isIfStatementField = baseNodeType === 'if' && fieldName === 'compare1'

					this.ensureNodeConfigExists(nodeType)
					if (this.nodeConfig[nodeType][fieldName] === undefined) {
						if (isRouteCallField || isIfStatementField) {
							this.nodeConfig[nodeType][fieldName] = undefined
						} else if (input.type === 'multi') {
							this.nodeConfig[nodeType][fieldName] = []
						} else if (input.type === 'multiKeyValue') {
							this.nodeConfig[nodeType][fieldName] = [{ key: '', value: '' }]
						} else if (input.type === 'checkbox' || input.type === 'boolean') {
							this.nodeConfig[nodeType][fieldName] = false
						} else if (input.type === 'number') {
							this.nodeConfig[nodeType][fieldName] = 0
						} else {
							this.nodeConfig[nodeType][fieldName] = ''
						}
					}

					inputs.push(input)
				})
			})

			return inputs
		},
		filteredInputsByGroup() {
			const result = {}

			if (!this.flowNodes || this.flowNodes.length === 0) {
				console.error('No flow nodes loaded in filteredInputsByGroup')
				return result
			}

			this.templateInputs.forEach((input) => {
				if (input.dependsOn) {
					const dependsOnField = this.templateInputs.find((field) => field.name === input.dependsOn)

					if (!dependsOnField || !this.nodeConfig[input.nodeType][input.dependsOn]) {
						return
					}
				}

				const group = input.group || 'General'
				if (!result[group]) {
					result[group] = []
				}
				result[group].push(input)
			})

			return result
		}
	},

	watch: {
		isOpen(newValue) {
			if (newValue) {
				this.resetModal()
				this.initialiseModal()
			}
		},
		template: {
			immediate: true,
			handler(template) {
				if (template && this.isOpen) {
					if (template.id && !template.template_fields) {
						this.fetchCompleteTemplate(template.id)
					} else {
						this.initialiseFromTemplate(template)
					}
				}
			}
		},
		flowNodes: {
			handler(newNodes) {
				if (newNodes && newNodes.length > 0 && Object.keys(this.templateFields || {}).length > 0) {
					this.nodesLoaded = true
				}
			}
		},
		'newFlow.tenantId': {
			handler() {
				if (this.isOpen && this.newFlow.tenantId) {
					this.loadResources()
				}
			}
		},
		'newFlow.accountId': {
			handler() {
				if (this.isOpen && this.newFlow.tenantId && this.newFlow.accountId) {
					this.loadResources()
				}
			}
		}
	},

	async mounted() {
		if (this.isOpen) {
			this.resetModal()
			this.initialiseModal()
		}
	},

	methods: {
		resetModal() {
			this.currentStep = 0
			this.templateId = null
			this.templateName = ''
			this.templateDescription = ''
			this.templateFields = {}
			this.flowNodes = []
			this.nodesLoaded = false
			this.files = []
			this.siptrunks = []
			this.mobileSims = []
			this.newFlow = {
				name: '',
				description: '',
				tenantId: null,
				flow_spec: {},
				accountId: null
			}
			this.nodeConfig = {}
			this.checkboxFields = {}
		},

		async initialiseModal() {
			if (!this.flowNodes || this.flowNodes.length === 0) {
				await this.loadNodeDefinitions()
			}

			if (this.flowNodes && this.flowNodes.length > 0) {
				await this.loadResources()
			}

			if (this.template) {
				if (this.template.id && !this.template.template_fields) {
					await this.fetchCompleteTemplate(this.template.id)
				} else {
					this.initialiseFromTemplate(this.template)
				}
			}

			this.validateTemplateFields()
		},

		validateTemplateFields() {
			if (!this.templateFields || Object.keys(this.templateFields).length === 0) {
				console.error('No template fields found after initialisation')
				return false
			}

			if (!this.flowNodes || this.flowNodes.length === 0) {
				console.error('No flow nodes loaded')
				return false
			}

			let valid = true
			Object.keys(this.templateFields).forEach((nodeType) => {
				const baseNodeType = this.getBaseNodeType(nodeType)
				const nodeDefinition = this.flowNodes.find((node) => node.type === baseNodeType)

				if (!nodeDefinition) {
					valid = false
					return
				}

				this.templateFields[nodeType].forEach((fieldName) => {
					const fieldDef =
						(nodeDefinition.fields && nodeDefinition.fields.find((f) => f.name === fieldName)) ||
						(nodeDefinition.properties && nodeDefinition.properties.find((p) => p.name === fieldName))

					if (!fieldDef) {
						if (nodeDefinition.defaultConfig && nodeDefinition.defaultConfig[fieldName] !== undefined) {
							this.ensureNodeConfigExists(nodeType)
							if (this.nodeConfig[nodeType][fieldName] === undefined) {
								this.nodeConfig[nodeType][fieldName] = nodeDefinition.defaultConfig[fieldName]
							}
							return
						}

						console.warn(
							`No field definition found for ${fieldName} in ${nodeType} (base: ${baseNodeType})`
						)

						this.ensureNodeConfigExists(nodeType)
						if (this.nodeConfig[nodeType][fieldName] === undefined) {
							this.nodeConfig[nodeType][fieldName] = ''
						}
					}
				})
			})

			return valid
		},

		async fetchCompleteTemplate(templateId) {
			try {
				const response = await flowTemplatesApi.getById(templateId)
				const templateData = response?.data ? response.data : response
				this.initialiseFromTemplate(templateData || this.template)
			} catch (error) {
				this.initialiseFromTemplate(this.template)
			}
		},

		async loadNodeDefinitions() {
			if (this.flowNodes && this.flowNodes.length > 0 && this.nodesLoaded) {
				return this.flowNodes
			}

			try {
				const response = await flowsApi.getAllNodes()

				if (response && Array.isArray(response)) {
					this.flowNodes = response
					this.nodesLoaded = true
				} else if (response && response.data && Array.isArray(response.data)) {
					this.flowNodes = response.data
					this.nodesLoaded = true
				} else {
					console.error('Invalid node definitions response format:', response)

					if (response && typeof response === 'object') {
						const possibleNodes = Object.values(response).find((val) => Array.isArray(val))
						if (possibleNodes && possibleNodes.length > 0) {
							this.flowNodes = possibleNodes
							this.nodesLoaded = true
						}
					}
				}
				return this.flowNodes
			} catch (error) {
				console.error('Error fetching node definitions:', error)
				return []
			}
		},

		initialiseFromTemplate(template) {
			if (!template) return

			this.newFlow.name = template.name || 'New Flow'
			this.newFlow.description = template?.description || ''

			this.newFlow.flow_spec = template.flow_spec || {}

			if (template.tenant?.id) {
				this.newFlow.tenantId = template.tenant.id
			} else if (this.tenants.length === 1) {
				this.newFlow.tenantId = this.tenants[0].id
			}

			if (template.account?.id) {
				this.newFlow.accountId = template.account.id
			} else if (this.newFlow.tenantId) {
				const tenantAccounts = this.accounts.filter((account) => account.tenant?.id === this.newFlow.tenantId)
				if (tenantAccounts.length === 1) {
					this.newFlow.accountId = tenantAccounts[0].id
				}
			}

			if (template.template_fields) {
				const validTemplateFields = {}

				if (template.flow_spec && template.flow_spec.nodes) {
					template.flow_spec.nodes.forEach((node) => {
						if (node.type) {
							const nodeTypePrefix = `${node.type}_${node.type}1`

							if (template.template_fields[node.type]) {
								validTemplateFields[nodeTypePrefix] = template.template_fields[node.type]
							} else if (template.template_fields[nodeTypePrefix]) {
								validTemplateFields[nodeTypePrefix] = template.template_fields[nodeTypePrefix]
							} else {
								const uuidKey = Object.keys(template.template_fields).find(
									(key) => key.includes('-') && key.startsWith(node.type)
								)

								if (uuidKey) {
									validTemplateFields[nodeTypePrefix] = template.template_fields[uuidKey]
								}
							}
						}
					})
				}

				this.templateFields = validTemplateFields
			} else {
				console.warn('No template_fields found in template:', template)

				if (template.flow_spec && template.flow_spec.nodes) {
					console.log('Attempting to extract template fields from flow_spec')
					this.extractTemplateFieldsFromFlowSpec(template.flow_spec)
				} else {
					this.templateFields = {}
				}
			}

			let hasNodeConfig = false

			const normalisedNodeConfig = {}

			if (template.nodeConfig && Object.keys(template.nodeConfig).length > 0) {
				hasNodeConfig = true

				if (template.flow_spec && template.flow_spec.nodes) {
					template.flow_spec.nodes.forEach((node) => {
						if (node.type) {
							const standardKey = `${node.type}_${node.type}1`

							const uuidKey = Object.keys(template.nodeConfig).find(
								(key) => key.includes('-') && key.startsWith(node.type)
							)

							if (uuidKey) {
								normalisedNodeConfig[standardKey] = template.nodeConfig[uuidKey]
							} else if (template.nodeConfig[node.type]) {
								normalisedNodeConfig[standardKey] = template.nodeConfig[node.type]
							} else if (template.nodeConfig[standardKey]) {
								normalisedNodeConfig[standardKey] = template.nodeConfig[standardKey]
							}
						}
					})
				}

				Object.keys(template.nodeConfig).forEach((key) => {
					if (!normalisedNodeConfig[key] && !key.includes('-')) {
						normalisedNodeConfig[key] = template.nodeConfig[key]
					}
				})
			}

			if (!hasNodeConfig && template.flow_spec && template.flow_spec.nodes) {
				template.flow_spec.nodes.forEach((node) => {
					if (node.type && node.config) {
						const standardKey = `${node.type}_${node.type}1`
						normalisedNodeConfig[standardKey] = node.config
						hasNodeConfig = true
					}
				})
			}

			if (hasNodeConfig) {
				this.initialiseNodeConfig(normalisedNodeConfig)
			} else {
				const defaultNodeConfig = {}

				Object.keys(this.templateFields).forEach((nodeType) => {
					defaultNodeConfig[nodeType] = {}

					this.templateFields[nodeType].forEach((fieldName) => {
						if (fieldName === 'promptText') {
							defaultNodeConfig[nodeType][fieldName] = ''
						} else if (fieldName === 'voice') {
							defaultNodeConfig[nodeType][fieldName] = {}
						} else {
							defaultNodeConfig[nodeType][fieldName] = ''
						}
					})
				})

				this.nodeConfig = defaultNodeConfig
			}

			this.initialiseCheckboxFields()

			if (this.newFlow.tenantId && this.newFlow.accountId) {
				this.loadResources()
			}
		},

		extractTemplateFieldsFromFlowSpec(flowSpec) {
			if (!flowSpec || !flowSpec.nodes || !Array.isArray(flowSpec.nodes)) {
				console.error('Invalid flow_spec format for extracting template fields')
				this.templateFields = {}
				return
			}

			const extractedFields = {}

			flowSpec.nodes.forEach((node) => {
				if (!node.type || !node.id) return

				const baseNodeType = this.getBaseNodeType(node.type)
				const nodeDefinition = this.flowNodes.find((def) => def.type === baseNodeType)
				if (!nodeDefinition) {
					return
				}

				const nodeKey = `${node.type}_${node.type}1`
				extractedFields[nodeKey] = []

				if (node.config) {
					Object.keys(node.config).forEach((key) => {
						if (['id', 'type', 'position', 'width', 'height', 'node_id'].includes(key)) return

						const fieldExists =
							(nodeDefinition.fields && nodeDefinition.fields.some((f) => f.name === key)) ||
							(nodeDefinition.properties && nodeDefinition.properties.some((p) => p.name === key))

						if (fieldExists && !extractedFields[nodeKey].includes(key)) {
							extractedFields[nodeKey].push(key)
						}
					})
				}

				if (extractedFields[nodeKey].length === 0) {
					delete extractedFields[nodeKey]
				}
			})

			this.templateFields = extractedFields
		},

		handleClose() {
			this.$emit('update:isOpen', false)
		},

		async loadResources() {
			try {
				if (!this.flowNodes || this.flowNodes.length === 0) {
					try {
						const nodesResponse = await flowsApi.getAllNodes()
						this.flowNodes = Array.isArray(nodesResponse) ? nodesResponse : []
						this.nodesLoaded = this.flowNodes.length > 0
					} catch (error) {
						console.error('Error loading node definitions:', error)
						this.flowNodes = []
					}
				}

				let hasFileInputs = false
				let hasSipTrunkInputs = false
				let hasMobileSimsInputs = false

				if (this.templateFields && this.flowNodes.length > 0) {
					Object.keys(this.templateFields).forEach((nodeType) => {
						const baseNodeType = this.getBaseNodeType(nodeType)
						const nodeDefinition = this.flowNodes.find((node) => node.type === baseNodeType)

						if (nodeDefinition) {
							this.templateFields[nodeType].forEach((fieldName) => {
								const fieldDef =
									(nodeDefinition.fields &&
										nodeDefinition.fields.find((f) => f.name === fieldName)) ||
									(nodeDefinition.properties &&
										nodeDefinition.properties.find((p) => p.name === fieldName))

								if (fieldDef) {
									if (fieldDef.type === 'file') hasFileInputs = true
									if (fieldDef.type === 'siptrunk') hasSipTrunkInputs = true
									if (fieldDef.type === 'mobilesim') hasMobileSimsInputs = true
								}
							})
						}
					})
				}

				if (hasFileInputs && this.newFlow.tenantId && this.newFlow.accountId) {
					try {
						const filesResponse = await filesApi.getAll({
							where: {
								selectedTenant: this.newFlow.tenantId,
								selectedAccount: this.newFlow.accountId
							}
						})

						this.files =
							filesResponse &&
							filesResponse.data &&
							filesResponse.data.results &&
							Array.isArray(filesResponse.data.results)
								? filesResponse.data.results
								: []
					} catch (error) {
						console.error('Error loading files:', error)
						this.files = []
					}
				}

				if (hasSipTrunkInputs && this.newFlow.tenantId && this.newFlow.accountId) {
					try {
						const siptrunksResponse = await siptrunksApi.getAll({
							where: {
								selectedTenant: this.newFlow.tenantId,
								selectedAccount: this.newFlow.accountId
							}
						})

						this.siptrunks =
							siptrunksResponse &&
							siptrunksResponse.data &&
							siptrunksResponse.data.results &&
							Array.isArray(siptrunksResponse.data.results)
								? siptrunksResponse.data.results
								: []
					} catch (error) {
						console.error('Error loading SIP trunks:', error)
						this.siptrunks = []
					}
				}

				if (hasMobileSimsInputs && this.newFlow.tenantId && this.newFlow.accountId) {
					try {
						const mobileSimsResponse = await mobileSimsApi.getAll({
							where: {
								selectedTenant: this.newFlow.tenantId,
								selectedAccount: this.newFlow.accountId
							}
						})

						this.mobileSims =
							mobileSimsResponse &&
							mobileSimsResponse.data &&
							mobileSimsResponse.data.results &&
							Array.isArray(mobileSimsResponse.data.results)
								? mobileSimsResponse.data.results
								: []
					} catch (error) {
						console.error('Error loading mobile SIMs:', error)
						this.mobileSims = []
					}
				}

				if (this.tenants.length === 1 && !this.newFlow.tenantId) {
					this.newFlow.tenantId = this.tenants[0].id
				}

				if (this.newFlow.tenantId) {
					const tenantAccounts = this.accounts.filter(
						(account) => account && account.tenant && account.tenant.id === this.newFlow.tenantId
					)

					if (tenantAccounts.length === 1 && !this.newFlow.accountId) {
						this.newFlow.accountId = tenantAccounts[0].id
					}
				}
			} catch (error) {
				console.error('Error in loadResources:', error)
			}
		},

		getBaseNodeType(nodeType) {
			if (nodeType.includes('_')) {
				return nodeType.split('_')[0]
			}
			return nodeType.replace(/[0-9a-f-]+$/, '')
		},

		getFieldType(nodeType, field) {
			if (!this.nodesLoaded) {
				console.warn(`getFieldType: flowNodes not loaded yet for ${nodeType}.${field}`)
				return 'text'
			}

			const baseNodeType = this.getBaseNodeType(nodeType)

			const node = this.flowNodes.find((n) => n.type === baseNodeType)

			if (!node) {
				console.warn(`getFieldType: No node definition found for ${baseNodeType}`)
				return 'text'
			}

			const fieldDef =
				(node.fields && node.fields.find((f) => f.name === field)) ||
				(node.properties && node.properties.find((p) => p.name === field))

			if (!fieldDef) {
				console.warn(`No field definition found for ${field} in ${nodeType} (base: ${baseNodeType})`)
				return 'text'
			}

			const type = fieldDef.type

			if (['code', 'code-javascript', 'code-json', 'code-html'].includes(type)) {
				return 'code'
			}

			if (['boolean', 'checkbox'].includes(type)) {
				return 'checkbox'
			}

			if (['time_condition', 'timeconditions'].includes(type)) {
				return 'time_condition'
			}

			if (type === 'array') {
				return 'multi'
			}

			if (type === 'multiselect') {
				return 'select'
			}

			return type
		},

		getFieldOptions(nodeType, field) {
			const baseNodeType = this.getBaseNodeType(nodeType)
			const node = this.flowNodes.find((n) => n.type === baseNodeType)

			if (!node) {
				return []
			}

			const fieldDef =
				(node.fields && node.fields.find((f) => f.name === field)) ||
				(node.properties && node.properties.find((p) => p.name === field))

			if (!fieldDef) {
				console.warn(`No field definition found for ${field} in ${nodeType} (base: ${baseNodeType})`)
				return []
			}
			return fieldDef.options || []
		},

		addOption(nodeType, field) {
			if (!this.nodeConfig[nodeType][field]) {
				this.nodeConfig[nodeType][field] = ['']
			} else {
				this.nodeConfig[nodeType][field].push('')
			}
		},

		addKeyValue(nodeType, field) {
			if (!this.nodeConfig[nodeType][field]) {
				this.nodeConfig[nodeType][field] = []
			}
			this.nodeConfig[nodeType][field].push({ key: '', value: '' })
		},

		onFileUploadClose(nodeType, fieldName, created) {
			this.isUploadOpen = false
			this.isRecordOpen = false

			if (created?.id) {
				this.files = [...this.files, created]
				this.nodeConfig[nodeType][fieldName] = created.id + '.' + created.fileExt
			}
		},

		resetNodeConfig() {
			if (!this.templateFields) {
				console.log('No template fields to reset node config')
				return
			}

			const nodeConfig = {}
			const nodeTypes = Object.keys(this.templateFields)

			for (const nodeType of nodeTypes) {
				nodeConfig[nodeType] = {}
				const fields = this.templateFields[nodeType]

				for (const fieldName of fields) {
					const baseNodeType = nodeType.replace(/\d+$/, '')

					const nodeDefinition = this.flowNodes.find((node) => node.type === baseNodeType)
					if (!nodeDefinition) {
						continue
					}

					const fieldDefinition =
						(nodeDefinition.fields && nodeDefinition.fields.find((f) => f.name === fieldName)) ||
						(nodeDefinition.properties && nodeDefinition.properties.find((p) => p.name === fieldName))

					if (!fieldDefinition) {
						console.log(`No field definition found for ${fieldName} in ${nodeType} (base: ${baseNodeType})`)
						continue
					}

					const isRouteCallField =
						baseNodeType === 'routecall' &&
						[
							'advanced_routing',
							'routing_style',
							'destinations',
							'destination',
							'whisper_prompt',
							'timeout'
						].includes(fieldName)
					const isIfStatementField = baseNodeType === 'if' && fieldName === 'compare1'

					const fieldType = this.getFieldType(nodeType, fieldName)
					if (isRouteCallField || isIfStatementField) {
						nodeConfig[nodeType][fieldName] = undefined
					} else if (fieldType === 'multi') {
						nodeConfig[nodeType][fieldName] = []
					} else if (fieldType === 'json' || fieldDefinition.type === 'object') {
						nodeConfig[nodeType][fieldName] = {}
					} else if (fieldType === 'multiKeyValue') {
						nodeConfig[nodeType][fieldName] = [{ key: '', value: '' }]
					} else if (
						fieldType === 'checkbox' ||
						fieldType === 'boolean' ||
						fieldDefinition.type === 'checkbox'
					) {
						nodeConfig[nodeType][fieldName] = false
					} else if (fieldType === 'number') {
						nodeConfig[nodeType][fieldName] = 0
					} else {
						nodeConfig[nodeType][fieldName] = ''
					}
				}
			}

			this.nodeConfig = nodeConfig
		},

		initialiseNodeConfig(templateNodeConfig) {
			const newNodeConfig = {}

			if (!this.templateFields || Object.keys(this.templateFields).length === 0) {
				this.nodeConfig = {}
				return
			}

			Object.keys(this.templateFields).forEach((nodeType) => {
				const baseNodeType = this.getBaseNodeType(nodeType)
				const nodeDefinition = this.flowNodes.find((node) => node.type === baseNodeType)
				if (!nodeDefinition) {
					return
				}

				newNodeConfig[nodeType] = {}
				;(this.templateFields[nodeType] || []).forEach((fieldName) => {
					const fieldDefinition =
						(nodeDefinition.fields && nodeDefinition.fields.find((f) => f.name === fieldName)) ||
						(nodeDefinition.properties && nodeDefinition.properties.find((p) => p.name === fieldName))

					if (!fieldDefinition) {
						this.ensureNodeConfigExists(nodeType)
						newNodeConfig[nodeType][fieldName] = ''
						return
					}

					const isRouteCallField =
						baseNodeType === 'routecall' &&
						[
							'advanced_routing',
							'routing_style',
							'destinations',
							'destination',
							'whisper_prompt',
							'timeout'
						].includes(fieldName)
					const isIfStatementField = baseNodeType === 'if' && fieldName === 'compare1'

					const fieldType = this.getFieldType(nodeType, fieldName)

					let templateNodeType = nodeType

					if (templateNodeConfig && !templateNodeConfig[nodeType]) {
						if (nodeType.includes('_')) {
							const [type, id] = nodeType.split('_')

							const uuidKey = Object.keys(templateNodeConfig).find((key) => {
								if (key.includes('-') && key.startsWith(type)) {
									const fieldsForThisNodeType = this.templateFields[nodeType] || []
									if (fieldsForThisNodeType.length > 0) {
										const fieldsForKey = Object.keys(templateNodeConfig[key])
										return fieldsForThisNodeType.some((field) => fieldsForKey.includes(field))
									}
									return true
								}
								return false
							})

							if (uuidKey) {
								templateNodeType = uuidKey
							} else {
								const oldFormatKey = Object.keys(templateNodeConfig).find(
									(key) => key.startsWith(type) && !key.includes('_')
								)
								if (oldFormatKey) {
									templateNodeType = oldFormatKey
								}
							}
						} else {
							const type = baseNodeType
							const standardisedKey = `${type}_${type}1`

							if (templateNodeConfig[standardisedKey]) {
								templateNodeType = standardisedKey
							} else {
								const newFormatKey = Object.keys(templateNodeConfig).find((key) =>
									key.startsWith(type + '_')
								)
								if (newFormatKey) {
									templateNodeType = newFormatKey
								} else {
									const fieldsForThisNodeType = this.templateFields[nodeType] || []
									if (fieldsForThisNodeType.length > 0) {
										const uuidKey = Object.keys(templateNodeConfig).find((key) => {
											if (!key.includes('-') || !key.startsWith(type)) return false

											const fieldsForKey = Object.keys(templateNodeConfig[key])
											return fieldsForThisNodeType.some((field) => fieldsForKey.includes(field))
										})

										if (uuidKey) {
											templateNodeType = uuidKey
										}
									}
								}
							}
						}
					}

					if (templateNodeConfig?.[templateNodeType]?.[fieldName] !== undefined) {
						if (fieldDefinition.type === 'array' || fieldType === 'multi') {
							newNodeConfig[nodeType][fieldName] = [...templateNodeConfig[templateNodeType][fieldName]]
						} else if (fieldDefinition.type === 'object' || fieldType === 'json') {
							newNodeConfig[nodeType][fieldName] = JSON.parse(
								JSON.stringify(templateNodeConfig[templateNodeType][fieldName])
							)
						} else if (fieldType === 'multiKeyValue') {
							newNodeConfig[nodeType][fieldName] = templateNodeConfig[templateNodeType][fieldName]?.length
								? JSON.parse(JSON.stringify(templateNodeConfig[templateNodeType][fieldName]))
								: [{ key: '', value: '' }]
						} else if (
							fieldType === 'checkbox' ||
							fieldType === 'boolean' ||
							fieldDefinition.type === 'checkbox'
						) {
							newNodeConfig[nodeType][fieldName] = !!templateNodeConfig[templateNodeType][fieldName]
						} else {
							newNodeConfig[nodeType][fieldName] = templateNodeConfig[templateNodeType][fieldName]
						}
					} else {
						if (isRouteCallField || isIfStatementField) {
							newNodeConfig[nodeType][fieldName] = undefined
						} else if (fieldType === 'multi') {
							newNodeConfig[nodeType][fieldName] = fieldDefinition.default
								? [...fieldDefinition.default]
								: []
						} else if (fieldType === 'json' || fieldDefinition.type === 'object') {
							newNodeConfig[nodeType][fieldName] = fieldDefinition.default
								? JSON.parse(JSON.stringify(fieldDefinition.default))
								: {}
						} else if (fieldType === 'multiKeyValue') {
							newNodeConfig[nodeType][fieldName] = [{ key: '', value: '' }]
						} else if (
							fieldType === 'checkbox' ||
							fieldType === 'boolean' ||
							fieldDefinition.type === 'checkbox'
						) {
							newNodeConfig[nodeType][fieldName] = !!fieldDefinition.default
						} else if (fieldType === 'number') {
							newNodeConfig[nodeType][fieldName] = fieldDefinition.default || 0
						} else {
							newNodeConfig[nodeType][fieldName] = fieldDefinition.default || ''
						}
					}
				})
			})

			this.nodeConfig = newNodeConfig
		},

		canProceed() {
			if (this.currentStep === 0) {
				return this.newFlow.name && this.newFlow.tenantId && this.newFlow.accountId
			}

			if (this.currentStep === 1) {
				let valid = true

				const nodeTypes = Object.keys(this.templateFields || {})

				for (const nodeType of nodeTypes) {
					const baseNodeType = nodeType.replace(/\d+$/, '')

					if (!this.flowNodes.some((node) => node.type === baseNodeType)) {
						continue
					}

					const fields = this.templateFields[nodeType] || []

					for (const field of fields) {
						const hasField = this.flowNodes.some(
							(node) =>
								node.type === baseNodeType &&
								((node.properties && node.properties.some((prop) => prop.name === field)) ||
									(node.fields && node.fields.some((f) => f.name === field)))
						)

						if (!hasField) {
							continue
						}

						const fieldType = this.getFieldType(nodeType, field)

						const nodeDefinition = this.flowNodes.find((node) => node.type === baseNodeType)
						const fieldDefinition =
							(nodeDefinition?.fields && nodeDefinition.fields.find((f) => f.name === field)) ||
							(nodeDefinition?.properties && nodeDefinition.properties.find((p) => p.name === field))

						if (fieldDefinition && !fieldDefinition.required) {
							continue
						}

						if (fieldType === 'string' || fieldType === 'text' || fieldType === 'textarea') {
							if (this.nodeConfig[nodeType]?.[field] === '') {
								valid = false
							}
						} else if (fieldType === 'array') {
							if (
								!this.nodeConfig[nodeType]?.[field]?.length ||
								this.nodeConfig[nodeType]?.[field]?.some((item) => item === '')
							) {
								valid = false
							}
						} else if (fieldType === 'object') {
							if (field === 'compare1') {
								if (
									!this.nodeConfig[nodeType]?.[field]?.left ||
									!this.nodeConfig[nodeType]?.[field]?.right
								) {
									valid = false
								}
							}
						} else if (fieldType === 'multiKeyValue') {
							const hasValidPair = this.nodeConfig[nodeType]?.[field]?.some(
								(item) => item.key && item.value
							)
							if (!hasValidPair && this.nodeConfig[nodeType]?.[field]?.length > 1) {
								valid = false
							}
						}
					}
				}

				return valid
			}

			return true
		},

		canCreate() {
			return this.newFlow.name && this.newFlow.tenantId && this.newFlow.accountId
		},

		async createFlow() {
			try {
				if (!this.canCreate) {
					console.error('Cannot create flow: missing required fields')
					return
				}

				const flowData = {
					name: this.newFlow.name,
					description: this.newFlow.description,
					tenantId: this.newFlow.tenantId,
					accountId: this.newFlow.accountId,
					flow_spec: this.newFlow.flow_spec
				}

				if (Object.keys(this.nodeConfig).length > 0) {
					flowData.nodeConfig = this.nodeConfig
				}

				const flow = await this.generateFlow(this.template, this.nodeConfig)

				const finalData = {
					name: this.newFlow.name,
					tenant: {
						id: this.newFlow.tenantId
					},
					account: {
						id: this.newFlow.accountId
					},
					data: flow
				}

				this.closeModal()
				this.$emit('flow-created', finalData)
			} catch (error) {
				console.error('Error creating flow:', error)

				if (error.response && error.response.data && error.response.data.message) {
					console.error(`Server error: ${error.response.data.message}`)
				}
			}
		},

		async generateFlow(flowTemplate, data) {
			const { flow_spec, template_fields } = flowTemplate

			let flow = flow_spec

			flow.nodes = flow.nodes?.map((node) => {
				const nodeTypePrefix = `${node.type}_${node.type}1`

				if (data[nodeTypePrefix] && (template_fields[nodeTypePrefix] || template_fields[node.type])) {
					const fields = template_fields[nodeTypePrefix] || template_fields[node.type] || []
					fields.forEach((field) => {
						if (data[nodeTypePrefix][field] !== undefined) {
							node.config[field] = data[nodeTypePrefix][field]
						}
					})
				} else {
					const uuidKey = Object.keys(data).find((key) => key.includes('-') && key.startsWith(node.type))

					if (uuidKey && data[uuidKey]) {
						const fields = template_fields[nodeTypePrefix] || template_fields[node.type] || []
						fields.forEach((field) => {
							if (data[uuidKey][field] !== undefined) {
								node.config[field] = data[uuidKey][field]
							}
						})
					}
				}
				return node
			})

			return flow
		},

		ensureNodeConfigExists(nodeType) {
			if (!this.nodeConfig[nodeType]) {
				this.nodeConfig[nodeType] = {}
			}
			return true
		},

		getFieldValue(nodeType, field) {
			if (!this.nodeConfig[nodeType]) return null
			return this.nodeConfig[nodeType][field]
		},

		initialiseCheckboxFields() {
			this.checkboxFields = {}

			Object.keys(this.templateFields || {}).forEach((nodeType) => {
				const baseNodeType = nodeType.replace(/\d+$/, '')

				if (!this.flowNodes || this.flowNodes.length === 0) {
					return
				}

				const nodeDefinition = this.flowNodes.find((node) => node.type === baseNodeType)
				if (!nodeDefinition) {
					return
				}

				this.checkboxFields[nodeType] = {}
				;(this.templateFields[nodeType] || []).forEach((field) => {
					const fieldDefinition =
						(nodeDefinition.properties && nodeDefinition.properties.find((prop) => prop.name === field)) ||
						(nodeDefinition.fields && nodeDefinition.fields.find((f) => f.name === field))

					if (!fieldDefinition) {
						return
					}

					if (fieldDefinition.type === 'boolean') {
						this.checkboxFields[nodeType][field] = false
					}
				})
			})
		},

		closeModal() {
			this.$emit('update:isOpen', false)
			this.resetModal()
		}
	}
}
</script>
